import { shaderMaterial, Sphere } from "@react-three/drei";
import { extend, useFrame } from "@react-three/fiber";
import { useControls } from "leva";
import { useRef } from "react";
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  MultiplyBlending,
  NormalBlending,
  Uniform,
} from "three";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { useSelector } from "react-redux";

const PanelMaterialBis = shaderMaterial(
  {
    uFallOffThreshold: 0,
    uTexture: null,
    uTime: 0,
    uBorder: 1,
    uColor: new Color("#ffffff"),
    uStribes: 30,
  },
  `
  varying vec2 vUv; // Coordonnées de texture
  varying vec3 vPosition; // Position du vertex
  varying vec3 vNormal; // Normale du vertex
  uniform float uTime; // Temps pour l'animation

  void main() {
    vec4 modelPosition = modelMatrix * vec4(position, 1.0); // Position du modèle
    vec4 viewPosition = viewMatrix * modelPosition; // Position dans l'espace de vue
    vec4 projectionPosition = projectionMatrix * viewPosition; // Position projetée
    gl_Position = projectionPosition; // Position finale du vertex
    
    vec4 modelNormal = modelMatrix * vec4(normal, 0.0); // Normale du modèle
    
    vPosition = modelPosition.rgb; // Stocke la position
    vNormal =  modelNormal.rgb; // Stocke la normale
    vUv = uv; // Stocke les coordonnées de texture
  }`,
  `
    uniform float uFallOffThreshold; // Seuil de dégradé
    uniform float uTime; // Temps pour l'animation
    uniform float uBorder; // Indicateur de bord
    uniform sampler2D uTexture; // Texture à appliquer
    uniform vec3 uColor; // Couleur de base
    varying vec3 vPosition; // Position du vertex
    varying vec3 vNormal; // Normale du vertex
    varying vec2 vUv; // Coordonnées de texture

    void main() {
        float stribe = mod((vPosition.y - uTime*0.5)*3.,1.); // Calcul de la bande
        stribe = pow(stribe,3.0); // Applique une puissance pour l'effet

        float glitchTime = uTime*2.-vPosition.y; // Temps pour l'effet de glitch
        float glitchStrength = sin(glitchTime) + sin(glitchTime*3.45) + sin(glitchTime*8.76)  + sin(glitchTime*6.76) + sin(glitchTime*14.76); // Force du glitch
        glitchStrength = smoothstep(0.,1.0,glitchStrength); // Lissage de la force
        glitchStrength *= 0.75; // Réduction de la force
        
        vec2 tmpUv = vec2(vUv.x, vUv.y); // Copie des coordonnées de texture
        // Si vous avez des transformations appliquées à tmpUv, commentez-les ou ajustez-les
        // tmpUv = vec2(1.0 - vUv.x, vUv.y * 1.1); // Inverser la coordonnée X pour le miroir vertical
        // tmpUv2 *= sin(uTime) * 0.2 + 1.; // Modifie les coordonnées pour l'effet de bord

        // Pour éviter le zoom/dézoom, assurez-vous que tmpUv et tmpUv2 ne sont pas modifiés
        vec2 tmpUv2 = tmpUv - .5; // Centrage des coordonnées de texture
        tmpUv2 += .5; // Restauration des coordonnées

        vec3 image = texture(uTexture, tmpUv2).rgb; // Récupération de la couleur de la texture
        image += glitchStrength*stribe*.05; // Ajout de l'effet de glitch
        image *= uColor; // Application de la couleur de base
    
        float strength = pow(max(abs(tmpUv.x - 0.5)*.925, abs(tmpUv.y - 0.5)),9.); // Calcul de la force d'effet
        strength *= 1.; // Amplification de la force
        strength = step(0.075, strength); // Seuil pour l'effet
        strength = 1.-strength; // Inversion de la force
        if(uBorder == 0.){
          strength = 1.; // Force maximale si pas de bord
        }

        gl_FragColor = vec4(image * strength * 0.9, 1.); // Réduire le multiplicateur final de 2 à 1.5
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
    }

`,
);

// shaderMaterial creates a THREE.ShaderMaterial, and auto-creates uniform setter/getters
// extend makes it available in JSX, in this case <portalMaterial />
extend({ PanelMaterialBis });
export default function HologramMaterialBis({ power = 1, uTexture, uBorder = 1, uStribes = 30 }) {
  const currentModule = useSelector((state) => state.progression.currentModule);
  const material = useRef();

  useFrame((state, delta, frame) => {
    if (material.current) {
      material.current.uniforms.uTime.value += delta;
      // material.current.uniforms.uColor.value.set(controls.color);
    }
  });

  return (
    <panelMaterialBis
      transparent={true}
      ref={material}
      side={DoubleSide}
      depthWrite={false}
      blending={NormalBlending}
      uTexture={uTexture}
      uStribes={uStribes}
      uBorder={uBorder}
    ></panelMaterialBis>
  );
}
